import type { TFunction } from 'i18next';
import {
  Boxes,
  ClipboardCheck,
  Hammer,
  Printer,
  Settings,
  ShoppingBag,
  Store,
  Tag,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { formatDuration } from 'utils/elapsed';
import { formatDateTime } from 'utils/formatDate';
import { isDeadlinePassed, parisToday } from 'utils/interventionDate';
import { apiErrorMessage } from 'utils/errorMessage';
import { ApiError } from 'utils/unwrapResponse';
import type {
  FollowUpRequest,
  InterventionRequest,
  InterventionRequestStatus,
} from 'api/interventionRequestsApi';
import {
  ASSIGNMENT_DOT,
  ASSIGNMENT_TONE,
  refusalDetail,
  statusState,
  technicianName,
} from 'pages/AdminIssues/components/upcomingAssignment';

/**
 * The words and colours of the follow-up screen — and **only what the ticket screen does not
 * already carry**. `refusalReasonLabel`, `refusalDetail`, `technicianName` and `interventionDay`
 * are imported from `upcomingAssignment` by every caller here: a second spelling of a refusal
 * would let the two screens disagree about the same row.
 */

/**
 * All five statuses, where the ticket screen's `assignmentLabel` only knows four *states* — it
 * folds `cancelled` and `superseded` into "nobody assigned", which is the right answer on a
 * mission row and the wrong one here. A follow-up is a history: a request that a reassignment
 * closed must say so, not vanish into « à affecter ».
 *
 * **One word each**, because the label lives in a 23 px pill: the sentences it used to carry
 * wrapped the chip onto two lines. The drawer is where a status is explained at length.
 */
export const followUpStatusLabel = (t: TFunction, status: InterventionRequestStatus): string => {
  switch (status) {
    case 'pending':
      return t('Pending');
    case 'accepted':
      return t('Confirmed');
    case 'declined':
      return t('Refused');
    case 'cancelled':
      return t('Reassigned');
    case 'superseded':
      // « Dépassée » and not « Pourvue »: the mission was filled, this request was overtaken —
      // and D46 now writes the status, so the word is read every day (L5b).
      return t('Superseded');
  }
};

/**
 * The same five statuses, said at length — the drawer's « Réponse du technicien » block, where
 * the subject is the answer rather than the row, and where there is room for more than one word.
 *
 * Two spellings on purpose: `followUpStatusLabel` above lives in a 23 px pill and has to be one
 * word, this one is read as a sentence. Merging them would break whichever surface lost.
 */
export const followUpAnswerLabel = (t: TFunction, status: InterventionRequestStatus): string => {
  switch (status) {
    case 'pending':
      return t('Awaiting an answer');
    case 'accepted':
      return t('Accepted');
    case 'declined':
      return t('Declined');
    case 'cancelled':
      return t('Reassigned');
    case 'superseded':
      return t('Superseded');
  }
};

/**
 * Why a row is terminal, said in words rather than left as an empty Actions cell. `null` when the
 * request is still live — the sentence would be a claim about a story that is not over.
 *
 * This is the dead end B-06 closed on the ticket screen, closed here too: the follow-up carries no
 * action (D36), so the only thing it owes a closed row is an explanation of why nothing is offered.
 */
export const terminalReason = (t: TFunction, status: InterventionRequestStatus): string | null => {
  switch (status) {
    case 'cancelled':
      return t('The back office reassigned this mission or released the technician.');
    case 'superseded':
      return t('Another technician accepted this mission first.');
    default:
      return null;
  }
};

/**
 * The tone comes from `statusState`, which already folds `cancelled` and `superseded` into
 * "nobody" — neither is a live assignment. A second mapping here would be a second place for
 * amber to stop meaning "waiting on somebody".
 */
export const statusTone = (status: InterventionRequestStatus): string =>
  ASSIGNMENT_TONE[statusState(status)];

export const statusDot = (status: InterventionRequestStatus): string =>
  ASSIGNMENT_DOT[statusState(status)];

/**
 * Whether the mission still exists in GPS — **read from the server's own join**, not guessed.
 *
 * It used to be `store || requestType || interventionDate`, and the guess was wrong in a way that
 * mattered: a real ticket with none of the three — the schema allows it, and historical
 * intervention dates are largely absent — lost both its links and was labelled as removed.
 */
export const missionKnown = (request: FollowUpRequest): boolean => request.missionKnown;

/** Plain tinted text and no badge: the answer is a fact, the status beside it is the state. */
export interface ResponseSummary {
  text: string;
  tone: string;
}

/** « Répondu en 15 min » — `formatDuration`: how long it took, not how long ago it was. */
const answerDelay = (t: TFunction, request: InterventionRequest): string =>
  request.responseDelayS == null
    ? ''
    : t('Answered in {{duration}}', { duration: formatDuration(request.responseDelayS) });

/**
 * What came back, in one line (E6). A superseded row names its acceptor when the server found
 * one (« pourvue par {nom} »), else says only that nobody answered. Cancelled: `—`.
 */
export const responseSummary = (t: TFunction, request: FollowUpRequest): ResponseSummary => {
  switch (request.status) {
    case 'pending': {
      const waiting = `${t('Awaiting')} · ${formatDuration(request.elapsedSeconds)}`;
      const text =
        request.reminderCount > 0
          ? `${waiting} · ${t('reminded ×{{count}}', { count: request.reminderCount })}`
          : waiting;
      return { text, tone: 'text-amber-700 dark:text-amber-500' };
    }
    case 'accepted':
      return { text: answerDelay(t, request), tone: 'text-emerald-700 dark:text-emerald-400' };
    case 'declined':
      return { text: answerDelay(t, request), tone: 'text-rose-700 dark:text-rose-400' };
    case 'superseded':
      return {
        text: request.acceptorName
          ? t('No answer · filled by {{name}}', { name: request.acceptorName })
          : t('No answer'),
        tone: 'text-muted-foreground/70',
      };
    default:
      return { text: '', tone: '' };
  }
};

/**
 * The row's pill label, with E18's derived statuses — computed, never stored. « Échéance
 * dépassée » wins over « Relancée »; both stay on the pending (amber) tone.
 */
export const followUpRowStatusLabel = (
  t: TFunction,
  request: InterventionRequest,
  today: string = parisToday()
): string => {
  if (request.status === 'pending') {
    if (isDeadlinePassed(request.deadlineDate, today)) return t('Deadline passed');
    if (request.reminderCount > 0) return t('Reminded');
  }
  return followUpStatusLabel(t, request.status);
};

/** Gateway statuses: the API itself never answered. */
const NO_ANSWER_STATUSES = [502, 503, 504];

/** The load-error sub-line: the server's own coded reason when it answered, BLOCS' line otherwise. */
export const followUpLoadError = (t: TFunction, error: unknown): string =>
  error instanceof ApiError && !NO_ANSWER_STATUSES.includes(error.status)
    ? apiErrorMessage(t, error)
    : t('The server did not respond.');

/** Eight tints, and the referential has eight values — a ninth one still lands on a stable one. */
const SPECIALTY_TINTS = [
  'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-400',
  'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-400',
  'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400',
  'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
  'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-400',
];

const NEUTRAL_TINT = 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300';

/**
 * The referential as it stands (L1b). Keyed on the **name** because that is what the request
 * carries, accent-folded so « Électricité » and a seed written without its accent land together.
 */
const KNOWN_SPECIALTIES: Record<string, { tint: number; Icon: LucideIcon }> = {
  installation: { tint: 0, Icon: Wrench },
  merchandising: { tint: 1, Icon: ShoppingBag },
  impression: { tint: 2, Icon: Printer },
  menuiserie: { tint: 3, Icon: Hammer },
  'fabrication plv': { tint: 4, Icon: Boxes },
  electricite: { tint: 5, Icon: Zap },
  maintenance: { tint: 6, Icon: Settings },
  audit: { tint: 7, Icon: ClipboardCheck },
};

const fold = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/** Sum of the code points: a new referential value gets a stable colour, never a random one. */
const tintOf = (name: string): string => {
  let sum = 0;
  for (let index = 0; index < name.length; index += 1) sum += name.charCodeAt(index);
  return SPECIALTY_TINTS[sum % SPECIALTY_TINTS.length];
};

export interface SpecialtyTone {
  tint: string;
  Icon: LucideIcon;
}

/**
 * The tile and the chip of a competence share one tint, so the row reads as one object. A name we
 * do not know still gets a stable colour and a neutral icon; **no name at all gets zinc**, because
 * inventing a competence is worse than admitting the mission carries none (D21).
 */
export const specialtyTone = (name: string | null): SpecialtyTone => {
  if (!name) return { tint: NEUTRAL_TINT, Icon: Store };
  const known = KNOWN_SPECIALTIES[fold(name)];
  return known
    ? { tint: SPECIALTY_TINTS[known.tint], Icon: known.Icon }
    : { tint: tintOf(fold(name)), Icon: Tag };
};

/**
 * What one solicitation of a mission says about itself, in the drawer's list (L5b).
 *
 * The refusal shows its reason **without an hour** — the hour belongs to the assignment panel;
 * here the reader already has the chronology two cards below.
 */
export const solicitationPhrase = (
  t: TFunction,
  request: InterventionRequest,
  retainedName: string | null
): string => {
  switch (request.status) {
    case 'accepted':
      return request.respondedAt
        ? t('Accepted first on {{date}} · carries out the mission', {
            date: formatDateTime(request.respondedAt),
          })
        : t('Carries out the mission');
    case 'pending':
      return t('No answer for {{duration}}', { duration: formatDuration(request.elapsedSeconds) });
    case 'declined':
      return refusalDetail(t, request) ?? followUpAnswerLabel(t, 'declined');
    case 'superseded':
      // Named, because « dépassée » alone leaves the reader asking by whom. Without a retained
      // name — a filtered page that hides the acceptance — the plain fact is all we may claim.
      return retainedName
        ? t('Mission taken by {{name}} · can no longer accept', { name: retainedName })
        : t('Mission taken by another technician · can no longer accept');
    default:
      return followUpAnswerLabel(t, request.status);
  }
};

/**
 * Who ends up doing the mission, said as the drawer says it: a name, « personne pour l'instant »
 * while an answer is still possible, « personne, tous ont refusé » once none is (R-066).
 *
 * Read off the requests themselves rather than trusted from the row: the row carries the request
 * the server retained, which on a mission nobody accepted is simply the most recent one.
 */
export const retainedTechnician = (
  t: TFunction,
  requests: InterventionRequest[]
): { name: string | null; label: string } => {
  const accepted = requests.find((request) => request.status === 'accepted');
  if (accepted) {
    const name = technicianName(accepted);
    return { name, label: name };
  }
  const live = requests.some((request) => request.status === 'pending');
  return { name: null, label: live ? t('None yet') : t('None — everyone refused') };
};
